import type { InjectionKey } from "vue";

import { getCurrentInstance, inject } from "vue";

import type { Locale } from "./i18n";
import type { WidgetMode } from "./widget";

/** Настройки, с которыми партнёр монтирует виджет */
export interface WidgetSettings {
    /** Домен партнёра, по нему загружаются данные виджета */
    domain: string;
    locale: Locale;
    /** popup - модалка поверх страницы, embed - встраивание в элемент партнёра */
    mode: WidgetMode;
    /** id элемента на странице партнёра, в который встраивается виджет (только для embed) */
    root_element: string;
}

export interface WidgetCalcData {
    device_id: string;
    device_imei: string;
    /** Модель покупаемого устройства (null при tradeIn без устройства) */
    trade_up_model: string | null;
    /** Стоимость покупаемого устройства (null при tradeIn без устройства) */
    trade_up_price: string | null;
    /** Модель сдаваемого устройства */
    model: string;
    /** Стоимость сдаваемого устройства */
    price: string;
    /** Грейд сдаваемого устройства */
    grade: string;
    /** Номер сертификата */
    certificate: string;
}

export interface WidgetCartData extends Omit<WidgetCalcData, "certificate"> {
    brand: string;
    modelData: {
        brand: string;
        category: string;
        model: string;
        color: string;
        code: string;
        other: string | Record<string, string>;
    };
    /** Цена покупаемого устройства с учётом всех скидок */
    price: string;
    /** Размер скидки Trade-In */
    trade_in_price: string;
    /** Размер дополнительной скидки Trade-In */
    top_up_price: string | null;
    certificate?: string;
    signedDocumentUrl?: string;
    final_price: boolean;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    signingType: string;
}

/** Колбэки партнёра */
export interface WidgetApi {
    onLoad?: () => void;
    onDrop?: () => void;
    onCalc?: (data: WidgetCalcData) => void;
    onCart?: (data: WidgetCartData) => void;
}

/** Методы, которые партнёр вызывает у смонтированного виджета */
export interface WidgetMethods {
    /** Открыть виджет без покупаемого устройства (только сдача) */
    tradeIn: () => void;
}

export interface WidgetMountOptions {
    settings: Partial<WidgetSettings> & Pick<WidgetSettings, "domain">;
    api?: WidgetApi;
}

export interface WidgetContext {
    settings: Readonly<WidgetSettings>;
    api: Readonly<WidgetApi>;
}

const WIDGET_CONTEXT_KEY: InjectionKey<WidgetContext> = Symbol("widget-context");

/**
 * Provide the context on the app level, not on the root component: pinia runs store setup
 * inside app.runWithContext(), where inject() sees only app-level provides.
 * Must be called in the root component setup, before any store is used
 */
export function provideWidgetContext(context: WidgetContext): void {
    const instance = getCurrentInstance();

    if (!instance) {
        throw new Error("provideWidgetContext() must be called in the root component setup");
    }

    instance.appContext.app.provide(WIDGET_CONTEXT_KEY, context);
}

/** Настройки и колбэки партнёра текущего экземпляра виджета */
export function useWidgetContext(): WidgetContext {
    const context = inject(WIDGET_CONTEXT_KEY);

    if (!context) {
        throw new Error("useWidgetContext() must be called inside the widget app");
    }

    return context;
}
